import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, Loader2, RefreshCw, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { pollJob } from '../../utils/aiJobs';

export const LiteralText = ({ country, norm, onCite, citedKey }) => {
  const { api } = useAuth();
  const [res, setRes] = useState(null);
  const [q, setQ] = useState('');
  const [num, setNum] = useState('');
  const [busy, setBusy] = useState(false);
  const [skip, setSkip] = useState(0);

  const load = (params = {}) => api.get(`/legislative/legal-order/${country}/${norm.id}/text`, { params: { q: q || undefined, num: num || undefined, skip, ...params } })
    .then((r) => setRes(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'No se pudo cargar el texto'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOfficial = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/legislative/legal-order/${country}/${norm.id}/text/load`, null, { params: { force } });
      await pollJob(api, data.job_id, { interval: 2500 });
      toast.success('Texto oficial descargado');
      setSkip(0);
      await load({ skip: 0 });
    } catch (e) { toast.error(e.message || 'La fuente oficial no respondió'); } finally { setBusy(false); }
  };
  const doSearch = (e) => { e.preventDefault(); setSkip(0); load({ skip: 0 }); };
  const page = (d) => { const s = Math.max(0, skip + d); setSkip(s); load({ skip: s }); };

  if (!res) return <Loader2 className="h-4 w-4 animate-spin text-blue-400" />;
  if (!res.loaded) return (
    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400" data-testid={`literal-empty-${norm.id}`}>
      Aún no descargamos el texto literal de esta norma.
      <button onClick={() => fetchOfficial()} disabled={busy} data-testid={`literal-fetch-btn-${norm.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Traer desde la fuente oficial
      </button>
    </div>
  );
  return (
    <div className="space-y-3" data-testid={`literal-text-${norm.id}`}>
      <form onSubmit={doSearch} className="flex flex-wrap gap-2">
        <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="N° art." data-testid={`literal-num-input-${norm.id}`} className="w-24 rounded-lg border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm" />
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar palabra en el texto" data-testid={`literal-q-input-${norm.id}`} className="w-full rounded-lg border border-slate-700 bg-slate-950/60 py-1 pl-7 pr-2 text-sm" />
        </div>
        <button type="submit" data-testid={`literal-search-btn-${norm.id}`} className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-semibold text-white">Buscar</button>
      </form>
      <p className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        {res.total} artículos · descargado {new Date(res.meta.fetched_at).toLocaleDateString('es-CL')} desde la fuente oficial
        <button onClick={() => fetchOfficial(true)} disabled={busy} data-testid={`literal-refresh-btn-${norm.id}`} className="inline-flex items-center gap-1 text-blue-400 hover:underline">
          <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Consultar en vivo
        </button>
      </p>
      <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
        {res.articles.map((a) => (
          <article key={a.i} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3" data-testid={`literal-article-${norm.id}-${a.n}`}>
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-xs font-semibold text-amber-300">Artículo {a.n}
                {a.status === 'derogado' && <span data-testid={`literal-derogado-${norm.id}-${a.n}`} className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px] text-slate-200">Derogado</span>}
                {a.source === 'ia' && <span data-testid={`literal-ia-${norm.id}-${a.n}`} className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] text-amber-200">Versión IA · no oficial</span>}
              </span>
              <button onClick={() => onCite({ articulo: `Art. ${a.n}`, contenido: a.text }, `lit-${a.i}`)} data-testid={`literal-cite-${norm.id}-${a.n}`}
                className={`rounded-full border px-2 py-0.5 text-[11px] ${citedKey(`lit-${a.i}`) ? 'border-emerald-500 bg-emerald-500/20 text-emerald-100' : 'border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/10'}`}>
                {citedKey(`lit-${a.i}`) ? '✓ Insertado' : '+ Insertar texto literal'}
              </button>
            </div>
            <p className="whitespace-pre-line font-serif text-sm leading-relaxed text-slate-200">{a.text}</p>
          </article>
        ))}
      </div>
      {res.total > 30 && (
        <div className="flex justify-between text-xs">
          <button disabled={skip === 0} onClick={() => page(-30)} data-testid={`literal-prev-btn-${norm.id}`} className="text-blue-400 disabled:opacity-40">← Anteriores</button>
          <span className="text-slate-500">{skip + 1}–{Math.min(skip + 30, res.total)} de {res.total}</span>
          <button disabled={skip + 30 >= res.total} onClick={() => page(30)} data-testid={`literal-next-btn-${norm.id}`} className="text-blue-400 disabled:opacity-40">Siguientes →</button>
        </div>
      )}
    </div>
  );
};
